import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { EnvelopeSimple, Lock, UserCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Auth({ mode }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isLogin = mode === "login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form.email, form.password, form.name);
        toast.success("Account created!");
      }
      navigate("/browse");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-[#FDFBF7] px-4 py-12">
      <div className="w-full max-w-md animate-fade-in-up opacity-0 stagger-1">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-2" data-testid="auth-heading">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-[#5C665D]">
            {isLogin ? "Log in to continue borrowing." : "Join the sharing community."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#EAE7E0] rounded-2xl p-6 sm:p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="auth-error">
              {error}
            </div>
          )}

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Full Name</label>
              <div className="relative">
                <UserCircle size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665D]" />
                <input
                  type="text" required value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all outline-none"
                  placeholder="John Doe"
                  data-testid="auth-name-input"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Email</label>
            <div className="relative">
              <EnvelopeSimple size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665D]" />
              <input
                type="email" required value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all outline-none"
                placeholder="you@email.com"
                data-testid="auth-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Password</label>
            <div className="relative">
              <Lock size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665D]" />
              <input
                type="password" required value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all outline-none"
                placeholder="Min 6 characters"
                minLength={6}
                data-testid="auth-password-input"
              />
            </div>
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full bg-[#2C5545] text-white hover:bg-[#224235] rounded-full py-3 font-medium transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>{isLogin ? "Log In" : "Create Account"} <ArrowRight size={16} weight="bold" /></>
            )}
          </button>
        </form>

        <p className="text-center text-sm text-[#5C665D] mt-6">
          {isLogin ? (
            <>Don't have an account? <Link to="/register" className="text-[#2C5545] font-medium hover:underline" data-testid="auth-switch-link">Sign up</Link></>
          ) : (
            <>Already have an account? <Link to="/login" className="text-[#2C5545] font-medium hover:underline" data-testid="auth-switch-link">Log in</Link></>
          )}
        </p>
      </div>
    </main>
  );
}
